// src/components/AutomationForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faRobot,
    faSave,
    faCheck,
    faExclamationCircle,
    faAlignLeft,
    faBell,
    faEnvelope,
    faLayerGroup,
    faBolt,
    faInfoCircle,
    faBullseye
} from '@fortawesome/free-solid-svg-icons';

import './FormPage.css';

// Trigger events for display
const TRIGGER_EVENTS = {
    birthday: { label: 'Birthday', type: 'Celebration', description: 'Fires daily at 9am for each member whose birthday is today' },
    wedding_anniversary: { label: 'Wedding Anniversary', type: 'Celebration', description: 'Fires daily at 9am for members in families with an anniversary today' },
    first_login: { label: 'First App Login', type: 'Lifecycle', description: 'Fires when a member creates an account and links to your organization' },
    member_invite: { label: 'Directory Invite', type: 'Lifecycle', description: 'Sends when a member is added to the directory with an email address, or when their email is updated' },
    directory_update: { label: 'Directory Update Request', type: 'Manual', description: 'Send on demand to request members update their contact information' },
    re_engagement: { label: 'Re-Engagement', type: 'Manual', description: 'Send on demand to reach out to inactive members' },
    event_feedback: { label: 'Event Feedback', type: 'Manual', description: 'Send on demand to request feedback after an event' }
};

// Priority options
const PRIORITY_OPTIONS = [
    { value: 'high', label: 'High' },
    { value: 'normal', label: 'Normal' },
    { value: 'low', label: 'Low' }
];

function AutomationForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser, userOrgId, isAdmin } = useAuth();

    // Form state
    const [name, setName] = useState('');
    const [triggerType, setTriggerType] = useState('');
    const [triggerEvent, setTriggerEvent] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [channelPush, setChannelPush] = useState(true);
    const [channelEmail, setChannelEmail] = useState(true);
    const [priority, setPriority] = useState('normal');
    const [bypassQuietHours, setBypassQuietHours] = useState(false);

    // Targeting state (for manual automations)
    const [targetingType, setTargetingType] = useState('all');
    const [targetingGroupIds, setTargetingGroupIds] = useState([]);
    const [targetingFamilyIds, setTargetingFamilyIds] = useState([]);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [availableFamilies, setAvailableFamilies] = useState([]);

    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Redirect to communications if no id (create mode no longer supported)
    useEffect(() => {
        if (!id) {
            navigate('/communications');
        }
    }, [id, navigate]);

    // Fetch existing automation for edit mode
    useEffect(() => {
        if (id && currentUser && isAdmin) {
            fetchAutomation();
        }
    }, [id, currentUser, isAdmin]);

    const fetchAutomation = async () => {
        setIsFetching(true);
        try {
            const automationRef = doc(db, 'automations', id);
            const automationSnap = await getDoc(automationRef);

            if (!automationSnap.exists()) {
                setError('Automation not found.');
                return;
            }

            const automation = automationSnap.data();
            setName(automation.name || '');
            setTriggerType(automation.trigger?.type || '');
            setTriggerEvent(automation.trigger?.event || '');
            setSubject(automation.message?.subject || '');
            setBody(automation.message?.body || '');
            setChannelPush(automation.channels?.push ?? true);
            setChannelEmail(automation.channels?.email ?? true);
            setPriority(automation.priority || 'normal');
            setBypassQuietHours(automation.bypassQuietHours || false);

            // Load targeting for manual automations
            if (automation.trigger?.type === 'manual') {
                const targeting = automation.targeting || {};
                setTargetingType(targeting.type || 'all');
                setTargetingGroupIds(targeting.groupIds || []);
                setTargetingFamilyIds(targeting.familyIds || []);
                // Fetch available groups and families
                await fetchGroupsAndFamilies();
            }
        } catch (err) {
            console.error('Error fetching automation:', err);
            setError(err.message || 'Failed to load automation.');
        } finally {
            setIsFetching(false);
        }
    };

    const fetchGroupsAndFamilies = async () => {
        try {
            const groupsQuery = query(collection(db, 'group'), where('orgId', '==', userOrgId));
            const familiesQuery = query(collection(db, 'family'), where('orgId', '==', userOrgId), orderBy('surname'));
            const [groupsSnap, familiesSnap] = await Promise.all([getDocs(groupsQuery), getDocs(familiesQuery)]);
            setAvailableGroups(
                groupsSnap.docs
                    .map(d => ({ id: d.id, name: d.data().name || 'Unnamed' }))
                    .sort((a, b) => a.name.localeCompare(b.name))
            );
            setAvailableFamilies(
                familiesSnap.docs.map(d => ({ id: d.id, name: d.data().surname || 'Unnamed' }))
            );
        } catch (err) {
            console.error('Error fetching groups/families:', err);
        }
    };

    const validateForm = () => {
        setError(null);
        if (!name.trim()) {
            setError('Automation name is required.');
            return false;
        }
        if (!subject.trim()) {
            setError('Message subject is required.');
            return false;
        }
        if (!body.trim()) {
            setError('Message body is required.');
            return false;
        }
        if (!channelPush && !channelEmail) {
            setError('Please select at least one channel (Push or Email).');
            return false;
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm() || !currentUser || !isAdmin || !userOrgId) {
            if (!currentUser) setError('You must be logged in.');
            if (!isAdmin) setError('You must be an admin.');
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const automationData = {
                name: name.trim(),
                message: {
                    subject: subject.trim(),
                    body: body.trim()
                },
                channels: {
                    push: channelPush,
                    email: channelEmail
                },
                priority,
                bypassQuietHours,
                updatedAt: serverTimestamp()
            };

            // Include targeting for manual automations
            if (triggerType === 'manual') {
                automationData.targeting = { type: targetingType };
                if (targetingType === 'groups') {
                    automationData.targeting.groupIds = targetingGroupIds;
                }
                if (targetingType === 'families') {
                    automationData.targeting.familyIds = targetingFamilyIds;
                }
            }

            const automationRef = doc(db, 'automations', id);
            await updateDoc(automationRef, automationData);
            setSuccessMessage('Automation updated successfully!');

            setTimeout(() => {
                navigate('/communications', { state: { message: 'Automation updated.' } });
            }, 1500);
        } catch (err) {
            console.error('Error saving automation:', err);
            setError(err.message || 'Failed to save automation. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/communications" className="form-back-btn" title="Back to Communications">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Access Denied</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-card">
                        <div className="form-card-body">
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">
                                    Only administrators can manage automations.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isFetching) {
        return (
            <div className="form-page">
                <div className="form-page-header">
                    <div className="container">
                        <div className="form-page-header-content">
                            <div className="form-page-header-left">
                                <Link to="/communications" className="form-back-btn" title="Back to Communications">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <div>
                                    <h1 className="form-page-title">Loading...</h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="form-loading">
                        <div className="skeleton-header"></div>
                        <div className="skeleton-form"></div>
                    </div>
                </div>
            </div>
        );
    }

    const formInputsDisabled = isLoading || !!successMessage;
    const isMemberInvite = triggerEvent === 'member_invite';
    const triggerInfo = TRIGGER_EVENTS[triggerEvent];

    return (
        <div className="form-page">
            {/* Page Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to="/communications" className="form-back-btn" title="Back to Communications">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">Edit Automation</h1>
                                <p className="form-page-subtitle">Update automation settings</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Form Card */}
                <div className="form-card">
                    <div className="form-card-body">
                        {/* Error Alert */}
                        {error && (
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">{error}</div>
                            </div>
                        )}

                        {/* Success Alert */}
                        {successMessage && (
                            <div className="form-alert form-alert-success">
                                <FontAwesomeIcon icon={faCheck} className="form-alert-icon" />
                                <div className="form-alert-content">{successMessage}</div>
                            </div>
                        )}

                        {!successMessage && (
                            <form onSubmit={handleSubmit} noValidate>
                                {/* Basic Info Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faRobot} style={{ marginRight: '0.5rem' }} />
                                        Automation Details
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Name <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-field-input"
                                            placeholder="e.g., Birthday Greeting"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            disabled={formInputsDisabled}
                                            maxLength={100}
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                {/* Trigger Section (read-only) */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faBolt} style={{ marginRight: '0.5rem' }} />
                                        Trigger
                                    </h3>

                                    <div className="form-alert form-alert-secondary" style={{ marginBottom: '1rem' }}>
                                        <FontAwesomeIcon icon={faBolt} className="form-alert-icon" />
                                        <div className="form-alert-content">
                                            <strong>{triggerInfo?.type || triggerType}:</strong> {triggerInfo?.label || triggerEvent}
                                            <br />
                                            {isMemberInvite ? (
                                                <>This email is sent automatically when a member with an email address is added to your directory, or when a member's email address is updated. A "Create Your Free Account" button and app download link are always included at the end of the email.</>
                                            ) : (
                                                <>{triggerInfo?.description}</>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Targeting Section (manual automations only) */}
                                {triggerType === 'manual' && (
                                    <div className="form-section">
                                        <h3 className="form-section-title">
                                            <FontAwesomeIcon icon={faBullseye} style={{ marginRight: '0.5rem' }} />
                                            Targeting
                                        </h3>

                                        <div className="form-field">
                                            <label className="form-field-label">Who should receive this?</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                {[
                                                    { value: 'all', label: 'All Members' },
                                                    { value: 'groups', label: 'Specific Groups' },
                                                    { value: 'families', label: 'Specific Families' }
                                                ].map(option => (
                                                    <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="targetingType"
                                                            value={option.value}
                                                            checked={targetingType === option.value}
                                                            onChange={(e) => setTargetingType(e.target.value)}
                                                            disabled={formInputsDisabled}
                                                        />
                                                        {option.label}
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {targetingType === 'groups' && (
                                            <div className="form-field">
                                                <label className="form-field-label">Select Groups</label>
                                                {availableGroups.length === 0 ? (
                                                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No groups found.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                        {availableGroups.map(group => (
                                                            <label
                                                                key={group.id}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.375rem',
                                                                    padding: '0.375rem 0.75rem',
                                                                    borderRadius: '20px',
                                                                    border: `1px solid ${targetingGroupIds.includes(group.id) ? '#3b82f6' : '#e2e8f0'}`,
                                                                    background: targetingGroupIds.includes(group.id) ? '#eff6ff' : 'white',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.85rem',
                                                                    transition: 'all 0.15s ease'
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={targetingGroupIds.includes(group.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setTargetingGroupIds(prev => [...prev, group.id]);
                                                                        } else {
                                                                            setTargetingGroupIds(prev => prev.filter(id => id !== group.id));
                                                                        }
                                                                    }}
                                                                    disabled={formInputsDisabled}
                                                                    style={{ display: 'none' }}
                                                                />
                                                                {group.name}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {targetingType === 'families' && (
                                            <div className="form-field">
                                                <label className="form-field-label">Select Families</label>
                                                {availableFamilies.length === 0 ? (
                                                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No families found.</p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                        {availableFamilies.map(family => (
                                                            <label
                                                                key={family.id}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.375rem',
                                                                    padding: '0.375rem 0.75rem',
                                                                    borderRadius: '20px',
                                                                    border: `1px solid ${targetingFamilyIds.includes(family.id) ? '#3b82f6' : '#e2e8f0'}`,
                                                                    background: targetingFamilyIds.includes(family.id) ? '#eff6ff' : 'white',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.85rem',
                                                                    transition: 'all 0.15s ease'
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={targetingFamilyIds.includes(family.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setTargetingFamilyIds(prev => [...prev, family.id]);
                                                                        } else {
                                                                            setTargetingFamilyIds(prev => prev.filter(id => id !== family.id));
                                                                        }
                                                                    }}
                                                                    disabled={formInputsDisabled}
                                                                    style={{ display: 'none' }}
                                                                />
                                                                {family.name}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Message Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faAlignLeft} style={{ marginRight: '0.5rem' }} />
                                        Message Content
                                    </h3>

                                    <div className="form-alert form-alert-info" style={{ marginBottom: '1rem' }}>
                                        <FontAwesomeIcon icon={faInfoCircle} className="form-alert-icon" />
                                        <div className="form-alert-content">
                                            <strong>Available variables:</strong> {'{{member.name}}'}, {'{{org.name}}'}{!isMemberInvite && <>, {'{{family.surname}}'}</>}
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Subject <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-field-input"
                                            placeholder="e.g., Happy Birthday, {{member.name}}!"
                                            value={subject}
                                            onChange={(e) => setSubject(e.target.value)}
                                            disabled={formInputsDisabled}
                                            maxLength={200}
                                        />
                                        <div style={{ textAlign: 'right', fontSize: '0.8rem', color: subject.length > 180 ? '#dc2626' : '#94a3b8', marginTop: '0.25rem' }}>
                                            {subject.length}/200
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Body <span className="required">*</span>
                                        </label>
                                        <textarea
                                            className="form-field-input form-field-textarea"
                                            placeholder="Enter the message body..."
                                            value={body}
                                            onChange={(e) => setBody(e.target.value)}
                                            disabled={formInputsDisabled}
                                            rows={5}
                                            maxLength={2000}
                                        />
                                        <div style={{ textAlign: 'right', fontSize: '0.8rem', color: body.length > 1800 ? '#dc2626' : '#94a3b8', marginTop: '0.25rem' }}>
                                            {body.length}/2000
                                        </div>
                                    </div>
                                </div>

                                {/* Channels Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.5rem' }} />
                                        Delivery Channels
                                    </h3>

                                    {isMemberInvite && (
                                        <div className="form-alert form-alert-secondary" style={{ marginBottom: '1rem' }}>
                                            <FontAwesomeIcon icon={faInfoCircle} className="form-alert-icon" />
                                            <div className="form-alert-content">
                                                Invite emails are email-only because the recipient doesn't have an account yet.
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Select Channels <span className="required">*</span>
                                        </label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isMemberInvite ? 'not-allowed' : 'pointer', opacity: isMemberInvite ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={channelPush}
                                                    onChange={(e) => setChannelPush(e.target.checked)}
                                                    disabled={formInputsDisabled || isMemberInvite}
                                                />
                                                <FontAwesomeIcon icon={faBell} style={{ color: '#3b82f6' }} />
                                                Push Notification
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isMemberInvite ? 'not-allowed' : 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={channelEmail}
                                                    onChange={(e) => setChannelEmail(e.target.checked)}
                                                    disabled={formInputsDisabled || isMemberInvite}
                                                />
                                                <FontAwesomeIcon icon={faEnvelope} style={{ color: '#10b981' }} />
                                                Email
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Priority & Options Section (hidden for invite emails) */}
                                {!isMemberInvite && <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '0.5rem' }} />
                                        Priority & Options
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Priority
                                        </label>
                                        <select
                                            className="form-field-select"
                                            value={priority}
                                            onChange={(e) => setPriority(e.target.value)}
                                            disabled={formInputsDisabled}
                                        >
                                            {PRIORITY_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-field">
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={bypassQuietHours}
                                                onChange={(e) => setBypassQuietHours(e.target.checked)}
                                                disabled={formInputsDisabled}
                                            />
                                            <span className="form-field-label" style={{ marginBottom: 0 }}>
                                                Bypass quiet hours
                                            </span>
                                        </label>
                                        <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                                            Send this message even during quiet hours (e.g., for time-sensitive greetings)
                                        </p>
                                    </div>
                                </div>}

                                {/* Submit Button */}
                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="form-submit-btn"
                                        disabled={formInputsDisabled}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSave} />
                                                Update Automation
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                        {successMessage && (
                            <div className="form-actions" style={{ borderTop: 'none', paddingTop: 0 }}>
                                <button
                                    type="button"
                                    className="form-submit-btn"
                                    onClick={() => navigate('/communications')}
                                >
                                    <FontAwesomeIcon icon={faRobot} />
                                    Go to Communications
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AutomationForm;
